import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

const USER_CACHE_KEY = 'mantle_user_cache_v1';

/**
 * Read a previously-cached user object out of localStorage. Used to
 * hydrate the auth state SYNCHRONOUSLY on app boot so we don't have to
 * wait for the /api/auth/me network round-trip before rendering the
 * dashboard. The cached copy is then validated in the background and
 * refreshed (or invalidated) once /auth/me returns.
 *
 * This is what eliminates the "black screen for 1-2 seconds on every
 * launch" feeling — especially noticeable on iOS home-screen icons
 * where every cold-start would otherwise wait on the network.
 *
 * Trade-off: a user whose session was revoked on the server will briefly
 * see the dashboard shell before being kicked back to /login. That's an
 * acceptable UX win — the server still enforces auth on every API call
 * so no protected data leaks.
 */
function readCachedUser() {
  try {
    if (!localStorage.getItem('access_token')) return null;
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.id) return parsed;
  } catch {
    // ignore parse errors — fall back to network check
  }
  return null;
}

function writeCachedUser(user) {
  try {
    if (user && typeof user === 'object') {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_CACHE_KEY);
    }
  } catch { /* quota / private-browsing */ }
}

export function AuthProvider({ children }) {
  // Hydrate synchronously from the cached user object (if any). Saves
  // the entire /auth/me roundtrip on every launch.
  const cached = readCachedUser();
  const [user, setUser] = useState(cached || null);
  const [loading, setLoading] = useState(!cached);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(false);
      writeCachedUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      writeCachedUser(data);
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      writeCachedUser(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Emergent Google Auth callback the URL
    // hash carries `session_id=xxx`. Skip the `/auth/me` check here so
    // AuthCallbackPage can exchange the session_id and issue our JWT
    // tokens FIRST — otherwise the /auth/me 401 kicks the user to /login
    // before the callback has a chance to run.
    if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    const { data } = await api.post('/auth/login', { username, password });
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
    }
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }
    setUser(data);
    writeCachedUser(data);
    // Remember this account on the device so "Switch Account" in the
    // sidebar can offer a one-tap hop back. No password / token is
    // stored — just a display roster.
    try {
      const { recordAccount } = await import('../utils/knownAccounts');
      recordAccount({
        username: data.username || username,
        name: data.name,
        workspace_name: data.workspace_name,
      });
    } catch { /* localStorage disabled — ignore */ }
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    writeCachedUser(null);
    // Wipe in-memory list caches so the next user (or after re-login as the
    // same user) doesn't see stale data from the previous session.
    try { (await import('../lib/cache')).clearAllCaches(); } catch {}
    // Also bust the plan-features module cache so a quick user-switch in
    // the same tab doesn't carry the previous workspace's gate map over.
    try { (await import('../hooks/usePlanFeatures')).clearPlanFeaturesCache(); } catch {}
    setUser(false);
  };

  const setSessionFromTokens = (access, refresh, userData) => {
    if (access) localStorage.setItem('access_token', access);
    if (refresh) localStorage.setItem('refresh_token', refresh);
    if (userData) {
      setUser(userData);
      writeCachedUser(userData);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, setSessionFromTokens }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
